import React, { useEffect, useRef } from 'react';
import MiniPlayerHeader from 'components/MiniPlayer/MiniPlayerHeader';
import MiniPlayerFooter from 'components/MiniPlayer/MiniPlayerFooter';
import miniPlayerSelector from 'selectors/miniPlayer';
import { miniplayerMode as MINIPLAYER_TYPE } from 'constants/InCall/InCallVideo';
import { toggleMiniPlayerVideo } from 'actions/InCall/InCallVideo';
import { useDispatch, useSelector } from 'react-redux';
import { hooks } from '@lifesize/clients.sdk';
import { injectIntl } from 'react-intl';
import 'styles/core.module.scss';
import classes from './MiniPlayer.module.scss';
import { useInterval } from 'hooks/useInterval';
import { CallLayout, CompositedLayout } from '@lifesize/clients.sdk/dist/components';
import { getMaximumReceiveStreams } from 'utils/mediaConstraints';

const updateVideoElement = async ({ miniplayerMode, remoteVideoStream, localVideoStream }, videoElement) => {
  // React doesn't treat srcObject as a prop
  const videoRef = videoElement.current;
  if (videoRef) {
    videoRef.srcObject =
      miniplayerMode === MINIPLAYER_TYPE.remote && remoteVideoStream ? remoteVideoStream : localVideoStream;
  }
};

const MiniPlayer = () => {
  const dispatch = useDispatch();
  const onEndCallClick = () => window.webClientApi.endCall();
  const onToggleAudioClick = () => window.webClientApi.toggleAudio();
  const onGoBackClick = () => window.desktopApp.activateMainWindow();
  const onSwitchViewClick = () => dispatch(toggleMiniPlayerVideo());

  const { miniplayerMode, isMiniPlayerOpen } = useSelector(miniPlayerSelector);
  const call = hooks.useCallState();
  const conferenceInfo = hooks.useConferenceInfo();
  const media = hooks.useMedia();
  const isModerator = hooks.useIsCurrentUserModerator();
  const isMutedByServer = conferenceInfo?.serverMuteState;
  const isAudioEnabled = !media?.localAudioMuted && !isMutedByServer;
  const remoteStreams = hooks.useRemoteStreams();
  const localStream = hooks.useLocalPrimaryStream();
  const remoteStreamMetadata = hooks.useRemoteStreamMetadata();
  const dominantStreamId = media?.dominantTalker;
  const remoteVideoStream = remoteStreams.length
    ? remoteStreams[dominantStreamId] || remoteStreams[0]
    : localStream.stream;
  const metadata = remoteStreamMetadata?.[remoteVideoStream?.id] || remoteStreamMetadata?.[dominantStreamId];
  const localVideoStream = localStream.stream;
  const startTime = call?.startTime;
  const title = conferenceInfo?.displayName;
  const throttleKiller = useRef<HTMLDivElement | null>(null);
  const videoElement = useRef<HTMLVideoElement | null>(null);
  const remoteAudioOnly = metadata?.videoPaused || metadata?.videoMuted;
  const { sfuSettings } = hooks.useAccountSettings();
  const isSfuMultiStream = getMaximumReceiveStreams() > 1 && !(sfuSettings?.sfuMultiStream === false);
  const LayoutComponent = isSfuMultiStream ? CallLayout : CompositedLayout;
  const { isWebConferencing } = useSelector((state: any) => {
    const isWebConferencing = state.app.call?.isWebConference;
    const speakers = state.app.mediaSettings?.speakers;

    return {
      isWebConferencing,
      speakers
    };
  });
  const callViewHolder = useRef(null);
  const videoLayout = {
    container: {
      width: 213,
      height: 120
    },
    videoPrimary: {
      ratio: 1.77,
      width: 213
    }
  };

  useInterval(
    () => {
      if (throttleKiller.current && isMiniPlayerOpen) {
        throttleKiller.current.textContent = Date.now().toString();
      }
    },
    1000 / 30,
    [isMiniPlayerOpen, throttleKiller.current]
  );

  useEffect(() => {
    // Don't update video source if remoteVideo is muted and mini player mode is remote.
    if (remoteAudioOnly && miniplayerMode === MINIPLAYER_TYPE.remote) return;
    const updateElement = async () => {
      await updateVideoElement({ miniplayerMode, remoteVideoStream, localVideoStream }, videoElement);
    };

    updateElement();
  }, [miniplayerMode, remoteVideoStream, localVideoStream, remoteAudioOnly]);

  if (!isMiniPlayerOpen) return null;

  return (
    <>
      <div className={classes.miniPlayerRoot}>
        <MiniPlayerHeader onGoBackClick={onGoBackClick} title={title} />
        <div className={classes.videoElementContainer} ref={callViewHolder}>
          <LayoutComponent
            muteRemotes={isWebConferencing}
            isMobileView={false}
            showMetadata={false}
            showPip={false}
            miniPlayerMode={true}
            isMinimized={true}
            Slider={false}
            // @ts-ignore
            videoLayout={videoLayout}
          />
        </div>
        <MiniPlayerFooter
          onSwitchViewClick={onSwitchViewClick}
          isAudioEnabled={isAudioEnabled}
          isModerator={isModerator}
          onEndCallClick={onEndCallClick}
          onToggleAudioClick={onToggleAudioClick}
          startTime={startTime}
          isMutedByServer={isMutedByServer}
        />
      </div>
      <div ref={throttleKiller} />
    </>
  );
};

export default injectIntl(MiniPlayer);
